/**
 * RAL Diagnostic - SECURE API Service
 */

const API_URL = process.env.REACT_APP_API_URL || '';

// =============================================================================
// Token Management
// =============================================================================

const getToken = () => localStorage.getItem('ral_token');

const setToken = (token) => {
  if (token) localStorage.setItem('ral_token', token);
};

const removeToken = () => {
  localStorage.removeItem('ral_token');
  localStorage.removeItem('ral_user');
};

// =============================================================================
// Input Sanitization (prevent XSS)
// =============================================================================

const sanitize = (input) => {
  if (typeof input !== 'string') return input;
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .trim();
};

// =============================================================================
// Secure Fetch Wrapper
// =============================================================================

const secureFetch = async (endpoint, options = {}) => {
  const url = `${API_URL}${endpoint}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
    
    if (response.status === 401) {
      removeToken();
      window.location.href = '/login';
      throw new Error('Session expired');
    }
    
    if (response.status === 429) {
      throw new Error('Too many requests. Please wait.');
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      const err = new Error(data.error || 'Request failed');
      err.fieldErrors = data;
      err.status = response.status;
      throw err;
    }
    
    return data;
    
  } catch (error) {
    console.error('API Error:', error.message);
    throw error;
  }
};

// =============================================================================
// Auth API
// =============================================================================

export const authAPI = {
  register: async (userData) => {
    const data = await secureFetch('/api/auth/register/', {
      method: 'POST',
      body: JSON.stringify({
        email: sanitize(userData.email),
        password: userData.password,
        confirm_password: userData.password2,
        company_name: sanitize(userData.company_name),
        contact_person: sanitize(userData.contact_name),
        country: sanitize(userData.country || 'Cyprus'),
        team_size: sanitize(userData.team_size || ''),
        num_projects: userData.num_projects || 0,
      }),
    });
    
    if (data.token) {
      setToken(data.token);
      localStorage.setItem('ral_user', JSON.stringify(data.user));
    }
    
    return data;
  },
  
  login: async (email, password) => {
    const data = await secureFetch('/api/auth/login/', {
      method: 'POST',
      body: JSON.stringify({
        email: sanitize(email),
        password: password,
      }),
    });
    
    if (data.token) {
      setToken(data.token);
      localStorage.setItem('ral_user', JSON.stringify(data.user));
    }
    
    return data;
  },
  
  logout: async () => {
    try {
      await secureFetch('/api/auth/logout/', { method: 'POST' });
    } finally {
      removeToken();
    }
  },
  
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const data = await secureFetch('/api/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    });
    
    if (data.token) {
      setToken(data.token);
    }
    
    return data;
  },
  
  isAuthenticated: () => !!getToken(),
  
  getUser: () => {
    const user = localStorage.getItem('ral_user');
    return user ? JSON.parse(user) : null;
  },
  
  getToken: getToken,
};

// =============================================================================
// Profile API
// =============================================================================

export const profileAPI = {
  get: () => secureFetch('/api/profile/'),
  
  update: (data) => {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      cleaned[key] = sanitize(data[key]);
    });
    
    return secureFetch('/api/profile/', {
      method: 'PATCH',
      body: JSON.stringify(cleaned),
    });
  },
};

// =============================================================================
// Contact API
// =============================================================================

export const contactAPI = {
  submit: (formData) => {
    return secureFetch('/api/contact/', {
      method: 'POST',
      body: JSON.stringify({
        name: sanitize(formData.name),
        email: sanitize(formData.email),
        company: sanitize(formData.company || ''),
        phone: sanitize(formData.phone || ''),
        location: sanitize(formData.location || ''),
        subject: sanitize(formData.subject),
        message: sanitize(formData.message),
        website: formData.website || '', // honeypot — must be empty
      }),
    });
  },
};

// =============================================================================
// Questionnaire API
// =============================================================================

export const questionnaireAPI = {
  getCategories: () => secureFetch('/api/categories/'),
  
  getQuestions: () => secureFetch('/api/questions/'),
  
  getDiagnostics: () => secureFetch('/api/diagnostics/'),
  
  createDiagnostic: (projectName = '') => secureFetch('/api/diagnostics/', {
    method: 'POST',
    body: JSON.stringify({ project_name: projectName }),
  }),
  
  getDiagnostic: (id) => secureFetch(`/api/diagnostics/${id}/`),
  
  submitResponses: (diagnosticId, responses) => {
    return secureFetch(`/api/diagnostics/${diagnosticId}/submit_responses/`, {
      method: 'POST',
      body: JSON.stringify({ responses }),
    });
  },
  
  getResponses: (diagnosticId) => secureFetch(`/api/diagnostics/${diagnosticId}/responses/`),
};

// =============================================================================
// NAMED EXPORTS (for backwards compatibility with existing components)
// =============================================================================

export const login = authAPI.login;
export const register = authAPI.register;
export const getProfile = profileAPI.get;

export const getCategories = questionnaireAPI.getCategories;
export const getQuestions = questionnaireAPI.getQuestions;
export const getQuestionsByCategory = questionnaireAPI.getQuestions;

export const getDiagnostics = questionnaireAPI.getDiagnostics;
export const getDiagnostic = questionnaireAPI.getDiagnostic;
export const createDiagnostic = questionnaireAPI.createDiagnostic;
export const submitResponses = questionnaireAPI.submitResponses;
export const getDiagnosticReport = questionnaireAPI.getDiagnostic;

// =============================================================================
// Default Export
// =============================================================================

const api = {
  auth: authAPI,
  profile: profileAPI,
  questionnaire: questionnaireAPI,
};

export default api;